import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, FileText, SquarePen } from 'lucide-react';
import Header from '@ajat/design-system/Header';
import Input from '@ajat/design-system/Input';
import Button from '@ajat/design-system/Button';
import { CheckboxMedium } from '@ajat/design-system/Checkbox';
import Hyperlink from '@ajat/design-system/Hyperlink';
import SidePanel from '@ajat/design-system/SidePanel';
import useCheckout from '../../Common/Checkout/useCheckout';
import OrderDetailsPanel from '../../Common/Checkout/OrderDetailsPanel';
import KustomCheckoutFrame from '../../Common/Checkout/KustomCheckoutFrame';
import { LegalSections } from '../../Common/HelpCenter';
import { PRIVACY_TITLE, PRIVACY_SECTIONS } from '../../Common/HelpCenter/legalContent';
import { copyright } from '../Common/Footer';
import useBrandSettings from '../../../../hooks/useBrandSettings';
import { useCart } from '../../../../context/CartContext';
import useSchoolClass from '../../../../hooks/useSchoolClass';
import useSchools from '../../../../hooks/useSchools';
import '../../Common/Checkout/Checkout.css';

/* ══════════════════════════════════════════════════════════════════════════
 * CLS-DK — Checkout Page
 * 2-step flow (no personnummer):
 *   1. Empty form (Danish labels)
 *   2. Compressed info + Delivery + Payment selection
 * ══════════════════════════════════════════════════════════════════════════ */

/* ── Delivery & Payment options ── */
const DELIVERY_OPTIONS = [
  {
    id: 'school',
    label: 'Levering til skole',
    description: 'Gratis fragt · Leveringstid: 19. jan. – 23. feb.',
  },
  {
    id: 'home',
    label: 'Hjemmelevering',
    description: 'Gratis fragt · Leveringstid: 19. jan. – 23. feb.',
  },
];

const NO_PAYMENT_METHODS_MESSAGE =
  'Onlinebetaling er ikke tilgængelig i øjeblikket, så din ordre kan ikke gennemføres. Prøv venligst igen senere, eller kontakt os.';

/* ── Validators (Danish messages) ── */
const VALIDATORS = {
  firstName: (v) => v?.trim() ? '' : 'Angiv fornavn',
  lastName:  (v) => v?.trim() ? '' : 'Angiv efternavn',
  email:     (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v || '') ? '' : 'Ugyldig e-mailadresse',
  phone:     (v) => /^\d{7,15}$/.test((v || '').replace(/[\s\-+]/g, '')) ? '' : 'Ugyldigt telefonnummer',
  address:   (v) => v?.trim() ? '' : 'Angiv adresse',
  // 4–6 digits: Danish postcodes have 4, German customers (5) ordered from DK too (2026-10-22).
  postnummer:(v) => /^\d{4,6}$/.test((v || '').replace(/\s/g, '')) ? '' : 'Postnummer skal være 4-6 cifre',
  city:      (v) => v?.trim() ? '' : 'Angiv by',
};

const INITIAL_FORM = {
  firstName: '', lastName: '', email: '', phone: '',
  address: '', postnummer: '', city: '',
};

/* ── Class options (collection-link carts only) ── */
const CLASS_NOT_IN_LIST = '__not_in_list__';
const CLASS_NOT_IN_LIST_OPTION = { label: 'Min klasse er ikke på listen', value: CLASS_NOT_IN_LIST };

const Checkout = () => {
  const navigate = useNavigate();
  const { paymentMethods, loading: brandSettingsLoading } = useBrandSettings('clsdk');
  const { invoiceFeeOverride, school: cartSchool, products: cartProducts, packages: cartPackages, total: cartTotal } = useCart('clsdk');
  /* Fully discounted cart (e.g. a 100% sales-rep code): the payment gateway
   * is skipped entirely — no method selection, no fee; the BE completes the
   * order as paid and the FE goes straight to the thank-you page. */
  const isFreeOrder = (Number(cartTotal) || 0) <= 0;
  const schoolClassName = useSchoolClass('clsdk');

  /* ── Collection-link carts: school + class + rep tick at checkout ──
   * Lines added from a /collection/{slug} link carry options.collectionSlug
   * and no school — this checkout then collects a REQUIRED school + class
   * and offers the rep tick (payload-only school binding: order stamping +
   * rep attribution, never pricing, so the collection tier holds). Every
   * other DK cart renders this checkout exactly as before — no picker, no
   * new validation. NOTE: `useSchools` ids are STRINGS.
   */
  const { schools } = useSchools('clsdk');
  const hasCollectionLine = useMemo(
    () => [...(cartProducts ?? []), ...(cartPackages ?? [])].some((it) => it?.options?.collectionSlug),
    [cartProducts, cartPackages],
  );
  const needsSchoolPicker = !cartSchool?.id && hasCollectionLine;
  const [selectedSchoolId, setSelectedSchoolId] = useState(null);
  const [schoolSearch, setSchoolSearch] = useState('');
  const [classSearch, setClassSearch] = useState('');
  const [classCustomMode, setClassCustomMode] = useState(false);
  const [contactedByRep, setContactedByRep] = useState(false);
  const SCHOOL_OPTIONS = useMemo(() => schools.map((s) => ({
    label: s.city ? `${s.name}, ${s.city}` : s.name,
    value: String(s.id),
  })), [schools]);
  const selectedSchool = useMemo(
    () => (needsSchoolPicker && selectedSchoolId
      ? schools.find((s) => String(s.id) === String(selectedSchoolId)) ?? null
      : null),
    [needsSchoolPicker, selectedSchoolId, schools],
  );
  const salesRep = selectedSchool?.salesRep ?? null;
  const CLASS_OPTIONS = useMemo(() => ([
    ...((selectedSchool?.classes ?? []).map((name) => ({ label: `Klasse ${name}`, value: name }))),
    CLASS_NOT_IN_LIST_OPTION,
  ]), [selectedSchool?.classes]);
  // School + class required only for collection-link carts (validators are
  // read live by useCheckout, so this may change when the cart loads).
  const validators = useMemo(() => (
    needsSchoolPicker
      ? {
          ...VALIDATORS,
          schoolId:  (v) => (v ? '' : 'Vælg skole'),
          className: (v) => (v?.trim() ? '' : 'Vælg klasse'),
        }
      : VALIDATORS
  ), [needsSchoolPicker]);

  /* ── Payment methods from brand settings (no fallback: an empty list
   *    means ordering is unavailable) ── */
  const paymentOptions = useMemo(() => (paymentMethods ?? []).map((m) => {
    const cfg = m.configPublic ?? {};
    const baseFee = Number(cfg.invoice_fee_amount) || 0;
    // Shop's invoice-fee override (if any) wins over the method's config fee.
    const fee = ('invoice_fee_amount' in cfg && invoiceFeeOverride != null) ? Number(invoiceFeeOverride) : baseFee;
    const feeLabel = cfg.invoice_fee_label || 'Fakturagebyr';
    const baseDescription = m.description ?? '';
    const description = fee > 0
      ? `${baseDescription}${baseDescription ? ' · ' : ''}${feeLabel}: +${Math.round(fee)},-`
      : baseDescription;
    return { id: m.id, key: m.key, label: m.name, description, fee, feeLabel };
  }), [paymentMethods, invoiceFeeOverride]);

  const {
    step, leaving,
    formData, setFormData,
    setTouched,
    delivery, setDelivery,
    payment, setPayment,
    checkingOut,
    checkoutError,
    iframePayment,
    cancelIframePayment,
    privacyOpen, setPrivacyOpen,
    detailsOpen, setDetailsOpen,
    total,
    updateField,
    isFieldValid, fieldError, handleBlur, handleFocus,
    validateAll,
    goToStep,
    handleCheckout,
    handleEditInfo,
  } = useCheckout({
    brandSlug: 'clsdk',
    brand: 'CLS',
    priceSuffix: ',-',
    thankYouRoute: '/clothing/cls-dk/thank-you',
    initialFormData: INITIAL_FORM,
    validators,
    editStep: 1,
    buildCheckoutPayload: ({ formData: fd, delivery: d, payment: p }) => ({
      payment_method_id:    isFreeOrder ? null : p,
      delivery_method:      d,
      school_id:            needsSchoolPicker && selectedSchoolId ? Number(selectedSchoolId) : null,
      school_class:         needsSchoolPicker ? (fd?.className ?? null) : (schoolClassName ?? null),
      contacted_by_rep:     salesRep ? contactedByRep : false,
      customer_first_name:  fd?.firstName ?? null,
      customer_last_name:   fd?.lastName ?? null,
      customer_email:       fd?.email ?? null,
      customer_phone:       fd?.phone ?? null,
      customer_address:     fd?.address ?? null,
      customer_postal_code: fd?.postnummer ?? null,
      customer_city:        fd?.city ?? null,
    }),
  });

  // Pre-select the first available payment method (customers rarely change
  // it, and an unselected radio blocked the CTA needlessly).
  useEffect(() => {
    if (payment == null && paymentOptions.length > 0) {
      setPayment(paymentOptions[0].id);
    }
  }, [payment, paymentOptions, setPayment]);

  /* ── Selected payment fee → adjusts displayed total ── */
  const selectedPaymentOption = useMemo(
    () => paymentOptions.find((o) => o.id === payment),
    [paymentOptions, payment],
  );
  const selectedFee = isFreeOrder ? 0 : (Number(selectedPaymentOption?.fee) || 0);
  const priceDisplay = `${Math.round((Number(total) || 0) + selectedFee)},-`;

  /* ── Step 1 → 2: validate form ── */
  const handleContinueToStep2 = useCallback(() => {
    if (validateAll()) goToStep(2, { scrollTop: true });
  }, [validateAll, goToStep]);

  const handleFormSubmit = useCallback((e) => {
    e.preventDefault();
    handleContinueToStep2();
  }, [handleContinueToStep2]);

  /* ── School + class picker handlers (collection-link carts only) ── */
  const handleSchoolSelect = useCallback((opt) => {
    if (!opt) {
      setSchoolSearch('');
      setSelectedSchoolId(null);
      setFormData((prev) => prev ? { ...prev, schoolId: '' } : prev);
      return;
    }
    setSchoolSearch(opt.label);
    setSelectedSchoolId(opt.value);
    setFormData((prev) => prev ? { ...prev, schoolId: opt.value } : prev);
  }, [setFormData]);

  const handleClassSearchChange = useCallback((e) => {
    const val = e.target.value;
    setClassSearch(val);
    setFormData((prev) => prev ? { ...prev, className: val } : prev);
    setTouched((prev) => ({ ...prev, className: false }));
  }, [setFormData, setTouched]);

  const handleClassSelect = useCallback((opt) => {
    if (!opt) {
      setClassSearch('');
      setFormData((prev) => prev ? { ...prev, className: '' } : prev);
      return;
    }
    if (opt.value === CLASS_NOT_IN_LIST) {
      setClassCustomMode(true);
      setClassSearch('');
      setFormData((prev) => prev ? { ...prev, className: '' } : prev);
      setTouched((prev) => ({ ...prev, className: false }));
      return;
    }
    setClassSearch(opt.label);
    setFormData((prev) => prev ? { ...prev, className: opt.value } : prev);
  }, [setFormData, setTouched]);

  const handleClassCustomChange = useCallback((e) => {
    const val = e.target.value;
    if (val === '') {
      setClassCustomMode(false);
      setClassSearch('');
      setFormData((prev) => prev ? { ...prev, className: '' } : prev);
      setTouched((prev) => ({ ...prev, className: false }));
      return;
    }
    setFormData((prev) => prev ? { ...prev, className: val } : prev);
    setTouched((prev) => ({ ...prev, className: false }));
  }, [setFormData, setTouched]);

  // Changing the picked school invalidates the class chosen for the old one.
  useEffect(() => {
    if (!needsSchoolPicker) return;
    setClassSearch('');
    setClassCustomMode(false);
    setFormData((prev) => prev ? { ...prev, className: '' } : prev);
    setTouched((prev) => ({ ...prev, className: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSchoolId]);

  /* ══════════════════════════════════════════════════════════════════════ */
  /* Render helpers                                                       */
  /* ══════════════════════════════════════════════════════════════════════ */

  const renderTotalPrice = () => (
    <div className="checkout__total">
      <span className="checkout__total-label typo-h6">I ALT</span>
      <span className="checkout__total-price typo-h2">{priceDisplay}</span>
      <Hyperlink variant="small" className="checkout__total-link" onClick={(e) => { e.preventDefault(); setDetailsOpen(true); }}>Se detaljer</Hyperlink>
    </div>
  );

  const renderPrivacyLink = () => (
    <div className="checkout__privacy">
      <Hyperlink variant="small" onClick={(e) => { e.preventDefault(); setPrivacyOpen(true); }}>{PRIVACY_TITLE}</Hyperlink>
    </div>
  );

  /* ── Step 1: Empty form (Danish labels) ── */
  const renderStep1 = () => (
    <form onSubmit={handleFormSubmit} className={`checkout__step-wrap${leaving ? ' checkout__step-wrap--leaving' : ''}`}>
      <div className="checkout__form-card">
        {/* Row 0: School + class — REQUIRED for collection-link carts only */}
        {needsSchoolPicker && (
          <>
            <div className="checkout__form-row">
              <div className="checkout__form-cell">
                <Input
                  type="searchable"
                  label="Skole"
                  name="school"
                  value={schoolSearch}
                  options={SCHOOL_OPTIONS}
                  onChange={(e) => setSchoolSearch(e.target.value)}
                  onSelect={handleSchoolSelect}
                  showIcon={false}
                  showOptionIcons={false}
                  showSuccess={Boolean(selectedSchoolId)}
                  error={fieldError('schoolId')}
                  onFocus={handleFocus('schoolId')}
                />
              </div>
            </div>
            <div className="checkout__form-row">
              <div className="checkout__form-cell">
                {!classCustomMode ? (
                  <Input
                    type="searchable"
                    label="Klassens navn"
                    name="class-name"
                    value={classSearch}
                    options={CLASS_OPTIONS}
                    onChange={handleClassSearchChange}
                    onSelect={handleClassSelect}
                    showIcon={false}
                    showOptionIcons={false}
                    showSuccess={isFieldValid('className')}
                    error={fieldError('className')}
                    onFocus={handleFocus('className')}
                  />
                ) : (
                  <Input
                    label="Klassens navn"
                    name="class-name"
                    value={formData?.className || ''}
                    showIcon={false}
                    showClear
                    showSuccess={isFieldValid('className')}
                    error={fieldError('className')}
                    onChange={handleClassCustomChange}
                    onBlur={handleBlur('className')}
                    onFocus={handleFocus('className')}
                  />
                )}
              </div>
            </div>
          </>
        )}

        {/* Row 1: First name | Last name */}
        <div className="checkout__form-row">
          <div className="checkout__form-cell">
            <Input
              id="shipping-first-name"
              label="Fornavn"
              name="fname"
              autoComplete="shipping given-name"
              value={formData.firstName}
              showIcon={false}
              showSuccess={isFieldValid('firstName')}
              error={fieldError('firstName')}
              onChange={updateField('firstName')}
              onBlur={handleBlur('firstName')}
              onFocus={handleFocus('firstName')}
            />
          </div>
          <div className="checkout__form-cell">
            <Input
              id="shipping-last-name"
              label="Efternavn"
              name="lname"
              autoComplete="shipping family-name"
              value={formData.lastName}
              showIcon={false}
              showSuccess={isFieldValid('lastName')}
              error={fieldError('lastName')}
              onChange={updateField('lastName')}
              onBlur={handleBlur('lastName')}
              onFocus={handleFocus('lastName')}
            />
          </div>
        </div>

        {/* Row 2: Email | Phone */}
        <div className="checkout__form-row">
          <div className="checkout__form-cell">
            <Input
              id="shipping-email"
              type="email"
              label="E-mail"
              name="email"
              autoComplete="shipping email"
              value={formData.email}
              showIcon={false}
              showSuccess={isFieldValid('email')}
              error={fieldError('email')}
              onChange={updateField('email')}
              onBlur={handleBlur('email')}
              onFocus={handleFocus('email')}
            />
          </div>
          <div className="checkout__form-cell">
            <Input
              id="shipping-phone"
              type="tel"
              label="Telefon"
              name="phone"
              autoComplete="shipping tel"
              value={formData.phone}
              showIcon={false}
              showSuccess={isFieldValid('phone')}
              error={fieldError('phone')}
              onChange={updateField('phone')}
              onBlur={handleBlur('phone')}
              onFocus={handleFocus('phone')}
            />
          </div>
        </div>

        {/* Row 3: Address (full width) */}
        <div className="checkout__form-row">
          <div className="checkout__form-cell">
            <Input
              id="shipping-address"
              label="Adresse"
              name="street-address"
              autoComplete="shipping street-address"
              value={formData.address}
              showIcon={false}
              showSuccess={isFieldValid('address')}
              error={fieldError('address')}
              onChange={updateField('address')}
              onBlur={handleBlur('address')}
              onFocus={handleFocus('address')}
            />
          </div>
        </div>

        {/* Row 4: Postnummer | City */}
        <div className="checkout__form-row">
          <div className="checkout__form-cell">
            <Input
              id="shipping-zip"
              label="Postnummer"
              name="zip"
              autoComplete="shipping postal-code"
              value={formData.postnummer}
              showIcon={false}
              showSuccess={isFieldValid('postnummer')}
              error={fieldError('postnummer')}
              onChange={updateField('postnummer')}
              onBlur={handleBlur('postnummer')}
              onFocus={handleFocus('postnummer')}
            />
          </div>
          <div className="checkout__form-cell">
            <Input
              id="shipping-city"
              label="By"
              name="city"
              autoComplete="shipping address-level2"
              value={formData.city}
              showIcon={false}
              showSuccess={isFieldValid('city')}
              error={fieldError('city')}
              onChange={updateField('city')}
              onBlur={handleBlur('city')}
              onFocus={handleFocus('city')}
            />
          </div>
        </div>
      </div>

      {renderPrivacyLink()}

      {/* Sales-rep referral tick — collection-link carts, rep schools only */}
      {salesRep && (
        <div className="checkout__rep-tick">
          <CheckboxMedium
            title={`Jeg blev kontaktet af ${salesRep.name}`}
            checked={contactedByRep}
            onChange={(e) => setContactedByRep(e.target.checked)}
          />
        </div>
      )}

      <div className="checkout__cta">
        <Button
          type="submit"
          size="primary"
          variant="alternative"
          iconRight={<ArrowRight size={20} />}
        >
          Fortsæt
        </Button>
      </div>

      {renderTotalPrice()}
    </form>
  );

  /* ── Step 2: Compressed + Delivery + Payment ── */
  const renderStep2 = () => (
    <div className={`checkout__step3${leaving ? ' checkout__step-wrap--leaving' : ''}`}>
      {/* Your Information */}
      <div className="checkout__step3-section">
        <h4 className="checkout__section-title typo-h4">Dine oplysninger</h4>
        <div className="checkout__compressed">
          <div className="checkout__compressed-details">
            <div className="checkout__compressed-icon-wrap">
              <FileText size={32} strokeWidth={1.5} />
            </div>
            <div className="checkout__compressed-copy">
              <span className="checkout__compressed-name typo-h4">
                {formData.firstName} {formData.lastName}
              </span>
              <div className="checkout__compressed-lines">
                <p>{formData.address}, {formData.city}, {formData.postnummer}, Danmark</p>
                <p>{formData.phone}, {formData.email}</p>
                {needsSchoolPicker && selectedSchool?.name && (
                  <p>{selectedSchool.name}{formData?.className ? `, ${formData.className}` : ''}</p>
                )}
              </div>
            </div>
          </div>
          <Button
            size="tertiary"
            variant="gray"
            iconOnly
            iconLeft={<SquarePen size={20} />}
            onClick={handleEditInfo}
          />
        </div>
        {renderPrivacyLink()}
      </div>

      {/* Delivery selection hidden — to be re-enabled later */}

      {/* Payment — hidden entirely for fully-discounted (free) orders:
          the BE places the order as paid without any gateway */}
      {!isFreeOrder && (
      <div className="checkout__step3-section">
        <h4 className="checkout__section-title typo-h4">Betaling</h4>
        {brandSettingsLoading ? (
          <p className="checkout__payment-loading typo-body">Indlæser betalingsmetoder…</p>
        ) : paymentOptions.length === 0 ? (
          <p className="checkout__error typo-body">
            {NO_PAYMENT_METHODS_MESSAGE}
          </p>
        ) : (
          <div className="checkout__radio-card">
            {paymentOptions.map((opt, i) => (
              <React.Fragment key={opt.id}>
                {i > 0 && <div className="checkout__radio-divider" />}
                <div
                  className={`checkout__radio-option${payment === opt.id ? ' checkout__radio-option--selected' : ''}`}
                  onClick={() => setPayment(opt.id)}
                  role="radio"
                  aria-checked={payment === opt.id}
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && setPayment(opt.id)}
                >
                  <div className="checkout__radio-main">
                    <div className="checkout__radio-circle">
                      <div className="checkout__radio-dot" />
                    </div>
                    <span className="checkout__radio-label typo-h4">{opt.label}</span>
                  </div>
                  <p className="checkout__radio-description typo-body">{opt.description}</p>
                </div>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
      )}

      {/* CTA */}
      <div className="checkout__cta checkout__cta--step3">
        <Button
          size="primary"
          variant="alternative"
          iconRight={<ArrowRight size={20} />}
          onClick={handleCheckout}
          loading={checkingOut}
          disabled={!isFreeOrder && !payment}
        >
          Gennemfør ordre
        </Button>
        {checkoutError && (
          <p className="checkout__error checkout__error--top-gap typo-body">
            {checkoutError}
          </p>
        )}
      </div>

      {renderTotalPrice()}
    </div>
  );

  /* ══════════════════════════════════════════════════════════════════════ */
  /* Main render                                                          */
  /* ══════════════════════════════════════════════════════════════════════ */
  return (
    <div className="checkout">
      <Header brand="CLS" mode="checkout" checkoutLabel="Sikker Betaling" backLabel="Tilbage" hideOnScroll={false} onBackClick={() => navigate('/clothing/cls-dk/shopping-cart')} />

      <main className="checkout__main">
        <div className="checkout__content">
          <h2 className="checkout__title typo-h2-editorial">Kassen</h2>
          {step === 1 && <h4 className="checkout__subtitle typo-h4">Dine oplysninger</h4>}

          {step === 1 && renderStep1()}
          {step === 2 && !iframePayment && renderStep2()}
          {step === 2 && iframePayment && (
            <div className="checkout__iframe-wrapper">
              <h4 className="checkout__section-title typo-h4">Gennemfør betaling</h4>
              <KustomCheckoutFrame
                loadingText="Sender dig til betaling…"
                snippet={iframePayment.snippet}
                onMessage={(e) => {
                  if (e?.data?.type === 'checkout_complete' || e?.data?.event === 'checkout_complete') {
                    navigate(`/clothing/cls-dk/thank-you?order=${encodeURIComponent(iframePayment.orderNumber)}&status=success`, {
                      state: { orderNumber: iframePayment.orderNumber },
                    });
                  }
                }}
              />
              <div style={{ marginTop: 16 }}>
                <Hyperlink onClick={cancelIframePayment}>Annuller og vælg en anden betalingsmetode</Hyperlink>
              </div>
            </div>
          )}
        </div>
      </main>

      <footer className="checkout__footer">
        <p className="checkout__footer-copy typo-body-sm">{copyright}</p>
      </footer>

      <OrderDetailsPanel
        isOpen={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        brandSlug="clsdk"
        priceSuffix=",-"
        title="Ordredetaljer"
        labels={{ products: 'Produkter', packages: 'Pakker', discount: 'Rabat', total: 'I alt' }}
        fee={selectedFee}
        feeLabel={selectedPaymentOption?.feeLabel ?? 'Fakturagebyr'}
        totalDisplay={priceDisplay}
      />

      <SidePanel isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} title={PRIVACY_TITLE}>
        <div className="checkout__privacy-content">
          <LegalSections sections={PRIVACY_SECTIONS} />
        </div>
      </SidePanel>
    </div>
  );
};

export default Checkout;
